import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, AlertTriangle, Loader2, Save } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import Button from '@/components/common/Button'
import Input from '@/components/common/Input'
import Select from '@/components/common/Select'
import Textarea from '@/components/common/Textarea'
import { recruiterJobOfferService } from '@/services/jobOfferService'
import { toast } from '@/stores/toastStore'
import { useQueryClient } from '@tanstack/react-query'
import { jobProspectsKeys } from '@/hooks/useJobProspects'
import { JobOffer, UpdateJobOfferRequest, EmploymentType, WorkMode } from '@/types'

const employmentTypeOptions = [
  { value: 'FULL_TIME', label: 'Full Time' },
  { value: 'PART_TIME', label: 'Part Time' },
  { value: 'CONTRACT', label: 'Contract' },
  { value: 'INTERNSHIP', label: 'Internship' },
]

const workModeOptions = [
  { value: 'REMOTE', label: 'Remote' },
  { value: 'HYBRID', label: 'Hybrid' },
  { value: 'ONSITE', label: 'Onsite' },
]

const currencyOptions = [
  { value: 'TND', label: 'TND (د.ت)' },
  { value: 'USD', label: 'USD ($)' },
  { value: 'EUR', label: 'EUR (€)' },
  { value: 'GBP', label: 'GBP (£)' },
  { value: 'CAD', label: 'CAD ($)' },
  { value: 'AUD', label: 'AUD ($)' },
]

export default function EditJobOfferPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { t } = useTranslation('jobOffers')
  const queryClient = useQueryClient()

  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [offer, setOffer] = useState<JobOffer | null>(null)
  const [formData, setFormData] = useState<UpdateJobOfferRequest>({
    position: '',
    location: '',
    employmentType: 'FULL_TIME',
    workMode: 'HYBRID',
    salaryMin: undefined,
    salaryMax: undefined,
    salaryCurrency: 'TND',
    benefits: '',
    message: '',
    responsibilities: '',
    requirements: '',
    expiresAt: '',
  })

  useEffect(() => {
    if (!id) return
    recruiterJobOfferService
      .getJobOfferDetails(id)
      .then((data) => {
        setOffer(data)
        setFormData({
          position: data.position,
          location: data.location,
          employmentType: data.employmentType,
          workMode: data.workMode,
          salaryMin: data.salaryMin,
          salaryMax: data.salaryMax,
          salaryCurrency: data.salaryCurrency,
          benefits: data.benefits,
          message: data.message,
          responsibilities: data.responsibilities,
          requirements: data.requirements,
          expiresAt: data.expiresAt?.split('T')[0],
        })
      })
      .catch(() => {
        toast.error('Failed to load job offer')
        navigate('/recruiter/job-offers')
      })
      .finally(() => setLoading(false))
  }, [id, navigate])

  const handleInputChange = (
    field: keyof UpdateJobOfferRequest,
    value: string | number | undefined
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.position || !formData.message) {
      toast.error(t('offers.errors.requiredFields'))
      return
    }
    try {
      setSubmitting(true)
      const submitData = { ...formData }
      if (submitData.expiresAt) {
        const date = new Date(submitData.expiresAt)
        date.setHours(23, 59, 59, 999)
        submitData.expiresAt = date.toISOString()
      }
      await recruiterJobOfferService.updateJobOffer(id!, submitData)
      queryClient.invalidateQueries({ queryKey: jobProspectsKeys.all })
      toast.success(t('offers.success.updated'))
      navigate('/recruiter/job-offers')
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('offers.errors.updateFailed'))
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <Button
            variant="secondary"
            onClick={() => navigate(-1)}
            className="mb-4 inline-flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <h1 className="text-2xl font-bold text-gray-900">{t('offers.update.title')}</h1>
          {offer && (
            <p className="text-gray-600 mt-1">
              Offer to{' '}
              <span className="font-semibold text-gray-800">
                {offer.candidate?.firstName} {offer.candidate?.lastName}
              </span>
            </p>
          )}
        </div>
        <Button
          type="submit"
          form="edit-offer-form"
          isLoading={submitting}
          disabled={submitting}
          className="flex items-center gap-2"
        >
          <Save className="w-4 h-4" />
          {t('offers.actions.saveChanges', 'Save Changes')}
        </Button>
      </div>

      <form id="edit-offer-form" onSubmit={handleSubmit} className="space-y-6">
        {/* Warning for VIEWED offers */}
        {offer?.status === 'VIEWED' && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-yellow-900 mb-1">
                {t('offers.update.warningTitle')}
              </h4>
              <p className="text-sm text-yellow-700">{t('offers.update.warningMessage')}</p>
            </div>
          </div>
        )}

        <div className="bg-white/70 backdrop-blur-sm border border-gray-200 rounded-2xl p-6 space-y-5">
          {/* Position */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('form.position')} <span className="text-red-500">*</span>
            </label>
            <Input
              type="text"
              value={formData.position}
              onChange={(e) => handleInputChange('position', e.target.value)}
              required
            />
          </div>

          {/* Location */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('form.location')}
            </label>
            <Input
              type="text"
              value={formData.location}
              onChange={(e) => handleInputChange('location', e.target.value)}
            />
          </div>

          {/* Employment Type & Work Mode */}
          <div className="grid grid-cols-2 gap-4">
            <Select
              label={t('form.employmentType')}
              value={formData.employmentType}
              onChange={(e) =>
                handleInputChange('employmentType', e.target.value as EmploymentType)
              }
              options={employmentTypeOptions}
            />
            <Select
              label={t('form.workMode')}
              value={formData.workMode}
              onChange={(e) => handleInputChange('workMode', e.target.value as WorkMode)}
              options={workModeOptions}
            />
          </div>

          {/* Salary Range */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('form.salaryMin')}
              </label>
              <Input
                type="number"
                value={formData.salaryMin || ''}
                onChange={(e) =>
                  handleInputChange(
                    'salaryMin',
                    e.target.value ? Number(e.target.value) : undefined
                  )
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('form.salaryMax')}
              </label>
              <Input
                type="number"
                value={formData.salaryMax || ''}
                onChange={(e) =>
                  handleInputChange(
                    'salaryMax',
                    e.target.value ? Number(e.target.value) : undefined
                  )
                }
              />
            </div>
            <Select
              label={t('form.currency')}
              value={formData.salaryCurrency}
              onChange={(e) => handleInputChange('salaryCurrency', e.target.value)}
              options={currencyOptions}
            />
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('form.message')} <span className="text-red-500">*</span>
            </label>
            <Textarea
              value={formData.message}
              onChange={(e) => handleInputChange('message', e.target.value)}
              rows={4}
              required
            />
          </div>

          {/* Responsibilities */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('form.responsibilities')}
            </label>
            <Textarea
              value={formData.responsibilities}
              onChange={(e) => handleInputChange('responsibilities', e.target.value)}
              rows={3}
            />
          </div>

          {/* Requirements */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('form.requirements')}
            </label>
            <Textarea
              value={formData.requirements}
              onChange={(e) => handleInputChange('requirements', e.target.value)}
              rows={3}
            />
          </div>

          {/* Benefits */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('form.benefits')}
            </label>
            <Textarea
              value={formData.benefits}
              onChange={(e) => handleInputChange('benefits', e.target.value)}
              rows={3}
            />
          </div>

          {/* Expiration Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('form.expiresAt')}
            </label>
            <Input
              type="date"
              value={formData.expiresAt || ''}
              onChange={(e) => handleInputChange('expiresAt', e.target.value)}
              min={new Date().toISOString().split('T')[0]}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <Button
            type="submit"
            variant="primary"
            isLoading={submitting}
            disabled={submitting}
            className="flex-1"
          >
            {t('offers.actions.saveChanges', 'Save Changes')}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate(-1)}
            disabled={submitting}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  )
}
